import React, { FunctionComponent } from 'react';
import { Grid, Statistic, Header, Image } from 'semantic-ui-react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { pathOr } from 'ramda';

import { ClassProp } from 'helpers/types';
import { BrandAsset } from 'scenes/Home/components/MerchantCard/MerchantCard.helper';

import styles from './MerchantDetails.styles';

export type Merchant = {
  merchantName: string;
  logo: string;
  activeCampaign: boolean;
  firstLiveDate: string;
  hasAccountDetails: boolean;
  venueCounts: {
    venues: number;
    venuesWithTransactions: number;
  };
  brandAssets: BrandAsset;
};

type Props = {
  merchant: Merchant;
  classes: ClassProp<typeof styles>;
};

const renderOfferStatus = (
  active: boolean,
  classes: ClassProp<typeof styles>,
) => {
  const text = active ? 'Live' : 'Complete';
  return (
    <div className={classnames(classes.statusPill, { activePill: active })}>
      {text}
    </div>
  );
};

const renderMerchantDetails = (
  classes: ClassProp<typeof styles>,
  {
    merchantName,
    logo,
    activeCampaign,
    firstLiveDate,
    venueCounts,
    brandAssets,
  }: Merchant,
) => {
  const { venues, venuesWithTransactions } = venueCounts || {
    venues: null,
    venuesWithTransactions: null,
  };

  const merchantLogo = pathOr(logo, ['image', 'icon', 'path'], brandAssets);

  return (
    <Grid className={classes.container}>
      <Grid.Row>
        <Grid.Column className={classes.merchantMeta} textAlign="left">
          <Image src={merchantLogo} circular className={classes.image} />

          <Header as="h2" className={classes.merchantName}>
            {merchantName}
          </Header>
          {renderOfferStatus(activeCampaign, classes)}
        </Grid.Column>

        <Grid.Column className={classes.merchantStats}>
          <Statistic.Group>
            <Statistic>
              <Statistic.Label className={classes.label}>
                Locations
              </Statistic.Label>

              <Statistic.Value className={classes.value}>
                {venues ? (
                  <FormattedNumber value={venues} style="decimal" />
                ) : (
                  'N/A'
                )}
              </Statistic.Value>
            </Statistic>

            <Statistic>
              <Statistic.Label className={classes.label}>
                Locations with transactions
              </Statistic.Label>

              <Statistic.Value className={classes.value}>
                {venuesWithTransactions ? (
                  <FormattedNumber
                    value={venuesWithTransactions}
                    style="decimal"
                  />
                ) : (
                  'N/A'
                )}
              </Statistic.Value>
            </Statistic>

            <Statistic>
              <Statistic.Label className={classes.label}>
                Onboarded
              </Statistic.Label>

              <Statistic.Value className={classes.value}>
                {firstLiveDate && (
                  <FormattedDate
                    value={firstLiveDate}
                    year="2-digit"
                    month="short"
                    day="2-digit"
                  />
                )}
              </Statistic.Value>
            </Statistic>
          </Statistic.Group>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

const MerchantDetails: FunctionComponent<Props> = ({ merchant, classes }) =>
  renderMerchantDetails(classes, merchant);

export default withStyles(styles)(MerchantDetails);
